import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import App from './App.jsx'
import { AuthProvider } from './contexts/AuthContext.jsx'
import { NotesProvider } from './contexts/NotesContext.jsx'
import { FoldersProvider } from './contexts/FoldersContext.jsx'
import { ThemeProvider } from './contexts/ThemeContext.jsx'
import { SensitiveSessionProvider } from './contexts/SensitiveSessionContext.jsx'
import { LocalModeProvider } from './contexts/LocalModeContext.jsx'
import { CloudStorageProvider } from './contexts/CloudStorageContext.jsx'
import { SyncProvider } from './contexts/SyncContext.jsx'
import { StatusProvider } from './contexts/StatusContext.jsx'
import { registerSW, setupServiceWorkerListeners, setupConnectivityListeners } from './utils/serviceWorker.js'
import { initializeAMOLEDEnhancer } from './utils/amoledEnhancer.js'
import './styles/index.css'
import './utils/clearCache.js' // Import for global access and HMR error handling
import frontendSync from './services/frontendSync.js'

// Expose for debugging
window.frontendSync = frontendSync

// Dev-only: filter extremely noisy, repetitive console messages in the browser
if (import.meta && import.meta.env && import.meta.env.DEV) {
  // Load debug utilities for testing
  import('./services/debug.js')
  
}

/**
 * Main entry point for the Note Taking Application frontend.
 * 
 * Sets up React Router, Authentication Context, Notes Context, Local Mode Context, Cloud Storage Context, Sync Context, and global toast notifications.
 * Provides the foundation for the entire application with proper error boundaries.
 */

// Register service worker for offline functionality
registerSW()

// Setup service worker and connectivity listeners
setupServiceWorkerListeners()
setupConnectivityListeners()

// Initialize AMOLED enhancer for dynamic theme support
initializeAMOLEDEnhancer()

// Setup global handlers for service worker events
window.showUpdateNotification = () => {
  // Toast notification for app updates
  const toast = document.createElement('div')
  toast.innerHTML = `
    <div style="background: #1f2937; color: white; padding: 12px 16px; border-radius: 8px; margin: 8px;">
      <div style="font-weight: 500; margin-bottom: 4px;">Update Available</div>
      <div style="font-size: 14px; color: #d1d5db; margin-bottom: 8px;">A new version of the app is available.</div>
      <button onclick="window.location.reload()" style="background: #3b82f6; color: white; border: none; padding: 6px 12px; border-radius: 4px; cursor: pointer;">
        Reload to Update
      </button>
    </div>
  `
  document.body.appendChild(toast)
  
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast)
    }
  }, 10000)
}

window.showOfflineNotification = () => {
  console.log('📱 App: Offline functionality enabled')
}

window.onSyncComplete = ({ synced, remaining }) => {
  if (synced > 0) {
    console.log(`✅ Sync: ${synced} items synced successfully`)
  }
  if (remaining > 0) {
    console.log(`⏳ Sync: ${remaining} items still pending`)
  }
}

window.onOnline = () => {
  console.log('🌐 App: Connection restored')
}

window.onOffline = () => {
  console.log('🔌 App: Working offline')
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}
  >
    <ThemeProvider>
      <StatusProvider>
        <LocalModeProvider>
          <CloudStorageProvider>
            <AuthProvider>
              <SyncProvider>
                <SensitiveSessionProvider>
                  <FoldersProvider>
                    <NotesProvider>
                      <App />
                      <Toaster 
                        position="top-right"
                        toastOptions={{
                          duration: 4000,
                          style: {
                            background: 'rgba(31, 41, 55, 0.5)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '12px 16px',
                            fontSize: '14px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          },
                          success: {
                            style: {
                              background: 'rgba(16, 185, 129, 0.5)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: 'white',
                              border: '1px solid rgba(16, 185, 129, 0.3)'
                            }
                          },
                          error: {
                            style: {
                              background: 'rgba(239, 68, 68, 0.5)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: 'white',
                              border: '1px solid rgba(239, 68, 68, 0.3)'
                            }
                          },
                          loading: {
                            style: {
                              background: 'rgba(31, 41, 55, 0.5)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.1)'
                            }
                          }
                        }}
                      />
                    </NotesProvider>
                  </FoldersProvider>
                </SensitiveSessionProvider>
              </SyncProvider>
            </AuthProvider>
          </CloudStorageProvider>
        </LocalModeProvider>
      </StatusProvider>
    </ThemeProvider>
  </BrowserRouter>
) 